import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';

const STATUSES = ['Active','On Leave','Resigned','Terminated','Retired','Absconded'];
const TYPES = ['Full-Time','Contract','Intern','Consultant'];
const GENDERS = ['Male','Female','Other'];

const empty = {
  emp_id: '', full_name: '', email: '', phone: '', gender: '', dob: '',
  pan: '', aadhaar_last4: '', bank_name: '', bank_account_last4: '', address: '',
  designation: '', department: '', location: '', employment_type: 'Full-Time',
  grade: '', reporting_manager_id: '', joining_date: '', confirmation_date: '',
  exit_date: '', exit_reason: '', status: 'Active', notes: '',
};

export default function EmployeeForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canEdit } = useAuth();
  const editing = id && id !== 'new';
  const readOnly = editing && !canEdit('employees');
  const [form, setForm] = useState(empty);
  const [opts, setOpts] = useState({ departments: [], locations: [], designations: [] });
  const [err, setErr] = useState('');

  useEffect(() => {
    api.get('/employees/_meta/options').then(setOpts).catch(() => {});
    if (editing) {
      api.get(`/employees/${id}`).then(d => setForm({ ...empty, ...d })).catch(e => setErr(e.message));
    }
  }, [id, editing]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setErr('');
    if (!form.full_name) { setErr('Full Name is required.'); return; }
    try {
      const payload = { ...form };
      if (typeof payload.emp_id === 'string') {
        payload.emp_id = payload.emp_id.replace(/[\u00a0\u200b\u200c\u200d\ufeff]/g, '').trim();
      }
      Object.keys(payload).forEach(k => { if (payload[k] === '') payload[k] = null; });
      if (editing) {
        await api.put(`/employees/${id}`, payload);
      } else {
        const res = await api.post('/employees', payload);
        navigate(`/employees/${res.emp_id}`, { replace: true });
        return;
      }
      navigate('/employees');
    } catch (e) { setErr(e.message); }
  };

  const exitVisible = ['Resigned','Terminated','Retired','Absconded'].includes(form.status);

  return (
    <>
      <div className="page-header">
        <h1>{editing ? `Employee · ${form.emp_id || id}` : 'New Employee'}</h1>
      </div>
      <div className="page-content">
        <nav className="page-back-nav" aria-label="Back navigation">
          <Link to="/employees" className="page-back-link">
            ← Back to Employee Master
          </Link>
        </nav>
        {err && <div style={{ background: '#fff5f5', border: '1px solid #fc8181', color: '#c53030', padding: 8, borderRadius: 4, marginBottom: 12 }}>{err}</div>}
        <div className="card">
          <div className="card-header">Identity</div>
          <div className="form-grid">
            <div className="form-group"><label>Employee ID</label>
              <input value={form.emp_id} onChange={e => set('emp_id', e.target.value)} placeholder="Auto if blank (EMP-#####)" disabled={editing} />
            </div>
            <div className="form-group"><label>Full Name *</label>
              <input value={form.full_name} onChange={e => set('full_name', e.target.value)} disabled={readOnly} />
            </div>
            <div className="form-group"><label>Email</label>
              <input type="email" value={form.email || ''} onChange={e => set('email', e.target.value)} disabled={readOnly} />
            </div>
            <div className="form-group"><label>Phone</label>
              <input value={form.phone || ''} onChange={e => set('phone', e.target.value)} disabled={readOnly} />
            </div>
            <div className="form-group"><label>Gender</label>
              <select value={form.gender || ''} onChange={e => set('gender', e.target.value)} disabled={readOnly}>
                <option value="">—</option>{GENDERS.map(g => <option key={g}>{g}</option>)}
              </select>
            </div>
            <div className="form-group"><label>Date of Birth</label>
              <input type="date" value={form.dob || ''} onChange={e => set('dob', e.target.value)} disabled={readOnly} />
            </div>
          </div>
        </div>

        <div className="card" style={{ marginTop: 16 }}>
          <div className="card-header">Employment</div>
          <div className="form-grid">
            <div className="form-group"><label>Designation</label>
              <input list="desigs" value={form.designation || ''} onChange={e => set('designation', e.target.value)} disabled={readOnly} />
              <datalist id="desigs">{opts.designations.map(d => <option key={d} value={d} />)}</datalist>
            </div>
            <div className="form-group"><label>Department</label>
              <input list="depts" value={form.department || ''} onChange={e => set('department', e.target.value)} disabled={readOnly} />
              <datalist id="depts">{opts.departments.map(d => <option key={d} value={d} />)}</datalist>
            </div>
            <div className="form-group"><label>Location</label>
              <input list="locs" value={form.location || ''} onChange={e => set('location', e.target.value)} disabled={readOnly} />
              <datalist id="locs">{opts.locations.map(l => <option key={l} value={l} />)}</datalist>
            </div>
            <div className="form-group"><label>Employment Type</label>
              <select value={form.employment_type || ''} onChange={e => set('employment_type', e.target.value)} disabled={readOnly}>
                <option value="">—</option>{TYPES.map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div className="form-group"><label>Grade</label>
              <input value={form.grade || ''} onChange={e => set('grade', e.target.value)} disabled={readOnly} />
            </div>
            <div className="form-group"><label>Reporting Manager (Emp ID)</label>
              <input value={form.reporting_manager_id || ''} onChange={e => set('reporting_manager_id', e.target.value)} placeholder="EMP-#####" disabled={readOnly} />
            </div>
            <div className="form-group"><label>Joining Date</label>
              <input type="date" value={form.joining_date || ''} onChange={e => set('joining_date', e.target.value)} disabled={readOnly} />
            </div>
            <div className="form-group"><label>Confirmation Date</label>
              <input type="date" value={form.confirmation_date || ''} onChange={e => set('confirmation_date', e.target.value)} disabled={readOnly} />
            </div>
            <div className="form-group"><label>Status</label>
              <select value={form.status} onChange={e => set('status', e.target.value)} disabled={readOnly}>
                {STATUSES.map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
            {exitVisible && <div className="form-group"><label>Exit Date</label>
              <input type="date" value={form.exit_date || ''} onChange={e => set('exit_date', e.target.value)} disabled={readOnly} />
            </div>}
            {exitVisible && <div className="form-group"><label>Exit Reason</label>
              <input value={form.exit_reason || ''} onChange={e => set('exit_reason', e.target.value)} disabled={readOnly} />
            </div>}
          </div>
        </div>

        <div className="card" style={{ marginTop: 16 }}>
          <div className="card-header">KYC & Banking</div>
          <div className="form-grid">
            <div className="form-group"><label>PAN</label>
              <input value={form.pan || ''} onChange={e => set('pan', e.target.value.toUpperCase())} maxLength={10} disabled={readOnly} />
            </div>
            <div className="form-group"><label>Aadhaar (last 4)</label>
              <input value={form.aadhaar_last4 || ''} onChange={e => set('aadhaar_last4', e.target.value)} maxLength={4} disabled={readOnly} />
            </div>
            <div className="form-group"><label>Bank Name</label>
              <input value={form.bank_name || ''} onChange={e => set('bank_name', e.target.value)} disabled={readOnly} />
            </div>
            <div className="form-group"><label>Bank A/C (last 4)</label>
              <input value={form.bank_account_last4 || ''} onChange={e => set('bank_account_last4', e.target.value)} maxLength={4} disabled={readOnly} />
            </div>
            <div className="form-group" style={{ gridColumn: '1 / -1' }}><label>Address</label>
              <textarea rows={2} value={form.address || ''} onChange={e => set('address', e.target.value)} disabled={readOnly} />
            </div>
            <div className="form-group" style={{ gridColumn: '1 / -1' }}><label>Notes</label>
              <textarea rows={2} value={form.notes || ''} onChange={e => set('notes', e.target.value)} disabled={readOnly} />
            </div>
          </div>
        </div>

        {!readOnly && (
          <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
            <button className="btn btn-primary" onClick={save}>{editing ? 'Save' : 'Create'}</button>
          </div>
        )}
      </div>
    </>
  );
}
